'use client';
import MobileSwiperTab from '@components/Modules/ContentModule/Swiper/MobileTab';
import HeaderButtons from '@components/shared/Header/components/HeaderButtons';
import HeaderText from '@components/shared/Header/components/HeaderText';
import { GridColFullWidth, GridContainerColClasses } from '@constants/layout';
import useIsInView from '@lib/hooks/useIsInView';
import useIsMobile from '@lib/hooks/useIsMobile';
import { ContentType } from '@lib/sanity/queries/modules/content/types';
import IconButton from '@ui/IconButton/IconButton';
import PortableText from '@ui/Text/PortableText';
import Text, { TextVariant } from '@ui/Text/Text';
import Unicase from '@ui/Unicase/Unicase';
import cn from '@utils/cn';
import React, { useCallback, useRef, useState } from 'react';

type Props = {
  data: ContentType;
  imageVisuals?: Record<string, React.ReactNode>;
  iconVisuals?: Record<string, React.ReactNode>;
};
export default function ContentTabsClient({ data, imageVisuals, iconVisuals }: Props) {
  const [openKey, setOpenKey] = useState<number>(0);
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInView(containerRef);

  const TabButtons = useCallback(
    (content: NonNullable<ContentType['contentList']>[number], i: number) => (
      <button
        key={i}
        className={cn(
          'text-foreground hover:border-border border-b',
          'cursor-pointer flex-nowrap items-start px-5 py-2',
          {
            'border-foreground hover:border-foreground cursor-default': i === openKey,
            'border-background': i !== openKey,
          },
        )}
        onClick={() => setOpenKey(i)}
      >
        <Text content={content.tabTitle?.value} variant={TextVariant.SubtitleMD} />
      </button>
    ),
    [openKey],
  );

  if (!data.contentList?.length) {
    return;
  }
  const selectedContent = data.contentList[openKey];

  return (
    <div ref={containerRef} className={cn('mx-auto w-full', GridColFullWidth)}>
      <div className={cn('gap-8 sm:gap-y-5 lg:gap-y-6', GridContainerColClasses)}>
        <div
          className={cn(
            'col-span-4 flex flex-col gap-8 text-center sm:col-span-6',
            'justify-center sm:text-left md:col-span-7',
            'lg:col-span-9 lg:text-left overflow-hidden',
          )}
        >
          <HeaderText {...data} className="flex flex-col gap-2" />

          {isMobile && isInView ? (
            <MobileSwiperTab data={data} setOpenKey={setOpenKey} openKey={openKey} />
          ) : (
            <div className="flex gap-4 whitespace-nowrap sm:gap-2">
              {data.contentList?.map(TabButtons)}
              {data.tabButton?.url && (
                <IconButton
                  className="rounded-none cursor-pointer px-5 py-2 w-auto"
                  button={data.tabButton}
                  variant="link"
                />
              )}
            </div>
          )}
        </div>
        <TextContent data={data} openKey={openKey} iconVisuals={iconVisuals} />
        {openKey !== undefined && selectedContent?.visuals && (
          <div
            className={cn(
              'order-2 col-span-4 flex justify-center sm:order-1',
              'sm:col-span-6 md:col-span-4 lg:col-span-7',
            )}
          >
            {selectedContent?.visuals._id && imageVisuals?.[selectedContent?.visuals._id]}
          </div>
        )}
      </div>
    </div>
  );
}

type TextContentProps = {
  data: ContentType;
  openKey: number;
  iconVisuals?: Record<string, React.ReactNode>;
};

function TextContent({ data, openKey, iconVisuals }: TextContentProps) {
  if (!data.contentList?.length) {
    return;
  }
  const selectedContent = data.contentList[openKey];

  return (
    <div
      className={cn(
        'mb-8 h-full flex-col flex gap-[6px]',
        'order-1 col-span-4 justify-center sm:col-span-6',
        'sm:order-2 sm:mb-0 md:col-span-4 lg:col-span-5',
      )}
    >
      {data.contentList && openKey !== undefined && selectedContent && (
        <div className="flex flex-col justify-start gap-8">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Unicase content={selectedContent.unicase?.value} isPill={selectedContent.unicaseIsPill} />
              <Text content={selectedContent.title?.value} variant={TextVariant.TitleLG} as="h3" />
              <PortableText content={selectedContent.description} variant={TextVariant.TextBodyLG} />
            </div>
            <div className="flex flex-col gap-4">
              {selectedContent.detailsList &&
                selectedContent.detailsList.map((c, i) => (
                  <div key={i} className="flex gap-2">
                    {c.icon?._id && iconVisuals?.[c.icon._id]}
                    <PortableText content={c.description} variant={TextVariant.TextBodyLG} />
                  </div>
                ))}
            </div>
          </div>

          <HeaderButtons
            {...selectedContent}
            buttonLink={undefined}
            className="mb-0 flex w-full flex-col gap-4 self-start sm:mb-5 sm:flex-row sm:gap-8 md:mb-0"
          />
        </div>
      )}
    </div>
  );
}
